'use client'

import { useMutation } from '@apollo/client'
import { Alert, Box, Button } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSetRecoilState } from 'recoil'
import { StripeCheckPaymentAuthenticationDocument } from '~/__generated__/graphql'
import { useStripePromise } from '~/libs/stripe'
import { snackbarTextState } from '~/recoil/snackbar'

export const CardActionRequire = () => {
  const setSnackbarText = useSetRecoilState(snackbarTextState)
  const [stripeCheckPaymentAuthentication] = useMutation(StripeCheckPaymentAuthenticationDocument)
  const [secrets, setSecrets] = useState<{
    setupClientSecret: string
    paymentClientSecret: string
  } | null>(null)
  const stripePromise = useStripePromise()
  const { t } = useTranslation()

  useEffect(() => {
    ;(async () => {
      const { data } = await stripeCheckPaymentAuthentication()
      if (
        data?.stripeCheckPaymentAuthentication?.setupClientSecret &&
        data?.stripeCheckPaymentAuthentication?.paymentClientSecret
      ) {
        setSecrets({
          setupClientSecret: data.stripeCheckPaymentAuthentication.setupClientSecret,
          paymentClientSecret: data.stripeCheckPaymentAuthentication.paymentClientSecret,
        })
      }
    })()
  }, [stripeCheckPaymentAuthentication])

  const handleAuthenticate = useCallback(async () => {
    if (!secrets) {
      setSnackbarText(t('Failed to verify your payment information.'))
      return
    }

    const stripe = await stripePromise
    if (!stripe) {
      setSnackbarText(t('Failed to verify your payment information.'))
      return
    }

    const result = await stripe.confirmSetup({
      clientSecret: secrets.setupClientSecret,
      confirmParams: {
        return_url: window.location.href,
      },
      redirect: 'if_required',
    })
    if (result.error) {
      setSnackbarText(t('Failed to verify your payment information.'))
      return
    }

    const paymentResult = await stripe.confirmCardPayment(secrets.paymentClientSecret, {
      return_url: window.location.href,
    })
    if (paymentResult.error) {
      setSnackbarText(t('Failed to verify your payment information.'))
      return
    }

    setSnackbarText(t('Your payment information has been successfully verified.'))
    setSecrets(null)
  }, [secrets, stripePromise, setSnackbarText, t])

  if (!secrets) {
    return null
  }

  return (
    <Alert
      severity='warning'
      sx={{
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        borderRadius: 0,
      }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
        }}
      >
        {t('Your payment information is not yet verified. Please verify it.')}
        <Button color='inherit' size='small' onClick={handleAuthenticate}>
          {t('Verify')}
        </Button>
      </Box>
    </Alert>
  )
}
